import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import Decimal from "decimal.js";

import {
  useGetTotalMedicarePrescriptionPaymentPlanTransactions,
  useListMedicarePrescriptionPaymentPlanDueInvoicesQuery,
} from "@/App/api/medicarePrescriptionPaymentPlan";
import { useCalculateMemberMedicarePrescriptionPaymentPlanAccumulationBalances } from "@/App/api/member";

interface useBalanceReturn {
  isBalanceLoading: boolean;
  isBalanceError: boolean;
  hasBalance: boolean;
  hasProcessingPayments: boolean;
  invoiceBalance: Decimal;
  accumulationBalance: Decimal;
}

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.participationUseBalance";

export const useBalance = (planYear: number): useBalanceReturn => {
  const { t: translate } = useTranslation();

  const showAlert = useAlert();

  const {
    data: dueInvoices,
    isLoading: isLoadingDueInvoices,
    isError: isErrorInvoices,
  } = useListMedicarePrescriptionPaymentPlanDueInvoicesQuery(planYear);

  const {
    data: transactionsPaid,
    isLoading: isTotalTransactionsLoading,
    isError: isTotalTransactionsError,
  } = useGetTotalMedicarePrescriptionPaymentPlanTransactions(planYear);

  const {
    data: memberAccumulations,
    isLoading: isLoadingAccums,
    isError: isErrorAccums,
  } = useCalculateMemberMedicarePrescriptionPaymentPlanAccumulationBalances(true, planYear);

  useEffect(() => {
    if (isErrorInvoices) {
      showAlert({
        type: "error",
        style: "banner",
        message: translate(`${TRANSLATION_PATH}.unableToLoadInvoices`),
      });
    }
    if (isTotalTransactionsError) {
      showAlert({
        type: "error",
        style: "banner",
        message: translate(`${TRANSLATION_PATH}.unableToLoadTransactions`),
      });
    }
    if (isErrorAccums) {
      showAlert({
        type: "error",
        style: "banner",
        message: translate(`${TRANSLATION_PATH}.unableToLoadAccumulations`),
      });
    }
  }, [dueInvoices, isTotalTransactionsError, isErrorAccums]);

  const invoiceBalance = useMemo(() => {
    return (dueInvoices?.results || [])
      .map((invoice) => new Decimal(invoice.amount_due))
      .reduce((prev: Decimal, curr: Decimal) => {
        return curr.add(prev);
      }, new Decimal(0));
  }, [dueInvoices]);

  const accumulationsAmount = useMemo(() => {
    if (memberAccumulations?.accumulations) {
      return new Decimal(memberAccumulations?.accumulations);
    }
    return new Decimal(0);
  }, [memberAccumulations]);

  const accumulationBalance = useMemo(() => {
    const decimalTransactionsPaidAmount = new Decimal(transactionsPaid?.total_balance || "0");
    return Decimal.max(Decimal.sub(accumulationsAmount, decimalTransactionsPaidAmount), 0);
  }, [accumulationsAmount, transactionsPaid]);

  const hasOutstandingBalance = invoiceBalance.gt(0);
  const hasOutstandingAccumulations = accumulationBalance.gt(0);

  const isBalanceLoading = isTotalTransactionsLoading || isLoadingDueInvoices || isLoadingAccums;
  const hasBalance = hasOutstandingBalance || hasOutstandingAccumulations;
  const isBalanceError = isErrorInvoices || isTotalTransactionsError || isErrorAccums;
  const hasProcessingPayments = dueInvoices?.results.some((invoice) => invoice.status === "processing");

  return { isBalanceLoading, isBalanceError, hasBalance, hasProcessingPayments, invoiceBalance, accumulationBalance };
};
